/**
 * TrafficLightDisplay Component
 *
 * Animated, game-like traffic light visualization for purchase decisions.
 * Shows Green (Take Control), Blue (Think It Over), or Red (Sleep On It)
 * with satisfying animations and haptic feedback support.
 */

import { useMemo } from 'react';
import { Box, Typography, keyframes } from '@mui/material';

// Decision types matching API
export type TrafficLightDecision = 'green' | 'yellow' | 'red';

export interface TrafficLightDisplayProps {
  decision: TrafficLightDecision | null;
  confidence: number;
  isLoading?: boolean;
  size?: 'small' | 'medium' | 'large';
}

// Pulse animation for active light
const pulseAnimation = keyframes`
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 0 20px currentColor, 0 0 40px currentColor;
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 0 30px currentColor, 0 0 60px currentColor, 0 0 80px currentColor;
  }
`;

// Glow animation for background
const glowAnimation = keyframes`
  0%, 100% {
    opacity: 0.6;
  }
  50% {
    opacity: 1;
  }
`;

// Scanning animation while loading
const scanAnimation = keyframes`
  0% {
    transform: translateY(-100%);
    opacity: 0;
  }
  50% {
    opacity: 1;
  }
  100% {
    transform: translateY(100%);
    opacity: 0;
  }
`;

// Decision configuration
const decisionConfig = {
  green: {
    color: '#10B981', // Emerald-500
    glowColor: 'rgba(16, 185, 129, 0.4)',
    bgGradient: 'linear-gradient(135deg, #065F46 0%, #047857 50%, #10B981 100%)',
    label: 'TAKE CONTROL',
    emoji: '✅',
    message: 'Go for it!',
    lightIndex: 2,
  },
  yellow: {
    color: '#F59E0B', // Amber-500
    glowColor: 'rgba(245, 158, 11, 0.4)',
    bgGradient: 'linear-gradient(135deg, #92400E 0%, #D97706 50%, #F59E0B 100%)',
    label: 'THINK IT OVER',
    emoji: '🤔',
    message: 'Consider this...',
    lightIndex: 1,
  },
  red: {
    color: '#EF4444', // Red-500
    glowColor: 'rgba(239, 68, 68, 0.4)',
    bgGradient: 'linear-gradient(135deg, #7F1D1D 0%, #B91C1C 50%, #EF4444 100%)',
    label: 'SLEEP ON IT',
    emoji: '🌙',
    message: 'Not right now',
    lightIndex: 0,
  },
};

// Size configurations
const sizeConfig = {
  small: {
    lightSize: 48,
    spacing: 2,
    containerPadding: 2,
    fontSize: '0.875rem',
    emojiSize: '1.5rem',
  },
  medium: {
    lightSize: 72,
    spacing: 3,
    containerPadding: 3,
    fontSize: '1rem',
    emojiSize: '2rem',
  },
  large: {
    lightSize: 96,
    spacing: 4,
    containerPadding: 4,
    fontSize: '1.25rem',
    emojiSize: '3rem',
  },
};

export function TrafficLightDisplay({
  decision,
  confidence,
  isLoading = false,
  size = 'large',
}: TrafficLightDisplayProps) {
  const config = decision ? decisionConfig[decision] : null;
  const sizeValues = sizeConfig[size];

  // Memoize the light states
  const lights = useMemo(() => {
    const lightOrder: TrafficLightDecision[] = ['red', 'yellow', 'green'];
    return lightOrder.map((lightDecision, index) => {
      const isActive = decision === lightDecision;
      const lightConfig = decisionConfig[lightDecision];
      return {
        decision: lightDecision,
        isActive,
        color: lightConfig.color,
        glowColor: lightConfig.glowColor,
        index,
      };
    });
  }, [decision]);

  // Trigger haptic feedback when decision changes
  useMemo(() => {
    if (decision && 'vibrate' in navigator) {
      // Different vibration patterns for each decision
      const patterns = {
        green: [50, 30, 50], // Quick double tap - approval
        yellow: [100, 50, 100, 50, 100], // Triple pulse - consideration
        red: [200, 100, 200], // Strong double pulse - warning
      };
      try {
        navigator.vibrate(patterns[decision]);
      } catch {
        // Haptic not available, fail silently
      }
    }
  }, [decision]);

  // Build screen-reader announcement for current state (WCAG 1.4.1, 4.1.3)
  const getAccessibleStatus = () => {
    if (isLoading) return 'Analyzing your purchase, please wait.';
    if (!decision || !config) return 'Purchase decision pending.';
    return `${config.label}: ${config.message}. Confidence: ${Math.round(confidence * 100)}%.`;
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        py: sizeValues.containerPadding,
      }}
      role="status"
      aria-live="polite"
      aria-label={getAccessibleStatus()}
    >
      {/* Traffic Light Housing */}
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: sizeValues.spacing,
          p: sizeValues.containerPadding,
          borderRadius: 4,
          backgroundColor: 'rgba(0, 0, 0, 0.85)',
          boxShadow: config
            ? `0 0 60px ${config.glowColor}, 0 8px 32px rgba(0, 0, 0, 0.4)`
            : '0 8px 32px rgba(0, 0, 0, 0.4)',
          border: '4px solid rgba(255, 255, 255, 0.1)',
          transition: 'box-shadow 0.5s ease',
          overflow: 'hidden',
        }}
      >
        {/* Scanning animation overlay when loading */}
        {isLoading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(180deg, transparent, rgba(59, 130, 246, 0.5), transparent)',
              animation: `${scanAnimation} 1.5s ease-in-out infinite`,
              pointerEvents: 'none',
              zIndex: 1,
            }}
          />
        )}

        {/* Lights — purely decorative; meaning conveyed via aria-label on container */}
        {lights.map((light) => (
          <Box
            key={light.decision}
            aria-hidden="true"
            sx={{
              position: 'relative',
              width: sizeValues.lightSize,
              height: sizeValues.lightSize,
              borderRadius: '50%',
              backgroundColor: light.isActive ? light.color : 'rgba(50, 50, 50, 0.8)',
              color: light.color,
              border: `3px solid ${light.isActive ? light.color : 'rgba(80, 80, 80, 0.5)'}`,
              transition: 'all 0.3s ease',
              animation: light.isActive
                ? `${pulseAnimation} 2s ease-in-out infinite`
                : 'none',
              boxShadow: light.isActive
                ? `0 0 20px ${light.color}, 0 0 40px ${light.glowColor}`
                : 'inset 0 2px 8px rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {/* Inner glow for inactive lights */}
            {!light.isActive && (
              <Box
                sx={{
                  width: '70%',
                  height: '70%',
                  borderRadius: '50%',
                  backgroundColor: `${light.color}20`,
                  boxShadow: `inset 0 0 10px ${light.color}30`,
                }}
              />
            )}
          </Box>
        ))}
      </Box>

      {/* Decision Label and Message */}
      {decision && config && !isLoading && (
        <Box
          sx={{
            mt: sizeValues.spacing,
            textAlign: 'center',
            animation: `${glowAnimation} 2s ease-in-out infinite`,
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontWeight: 800,
              fontSize: sizeValues.emojiSize,
              mb: 1,
            }}
            aria-hidden="true"
          >
            {config.emoji}
          </Typography>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 800,
              color: config.color,
              textShadow: `0 0 20px ${config.glowColor}`,
              letterSpacing: '0.1em',
              fontSize: sizeValues.fontSize,
              textTransform: 'uppercase',
            }}
          >
            {config.label}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              mt: 1,
              color: 'text.secondary',
              fontSize: size === 'small' ? '0.75rem' : '0.875rem',
            }}
          >
            {Math.round(confidence * 100)}% confident
          </Typography>
        </Box>
      )}

      {/* Loading state message */}
      {isLoading && (
        <Box sx={{ mt: sizeValues.spacing, textAlign: 'center' }}>
          <Typography
            variant="body1"
            sx={{
              color: 'text.secondary',
              fontStyle: 'italic',
            }}
          >
            Analyzing your purchase...
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export default TrafficLightDisplay;
